import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SETUP_SAVE_MUTATION_KEY } from '@/core/components/reputation/setup/setup-save-status';
import type { Location } from '@up2stay/up2promo-api-client';
import { LocationsApi } from '@up2stay/up2promo-api-client';
import { toast } from 'sonner';

import { ReplyStyleEditor } from '@/core/components/reputation/setup/reply-style-editor';
import {
  ReputationSetupAutoReplyDefaults,
  ReputationSetupAutoReplyOverrides,
} from '@/core/components/reputation/setup/reputation-setup-auto-reply-section';
import { SetupOverrideList } from '@/core/components/reputation/setup/setup-override-list';
import { SettingsGroupLabel } from '@/core/components/settings/settings-group';
import { useLocations } from '@/core/hooks/use-locations';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

function hasReplyPromptOverride(location: Location): boolean {
  const config = location.replyPromptConfig;
  if (config == null) return false;
  return Boolean(
    config.templateId?.trim() ||
    config.tone?.trim() ||
    config.considerations?.trim() ||
    config.mustInclude?.trim() ||
    config.mergedContext?.trim(),
  );
}

/** Reply style (template / own wording + live preview) and automatic replies. */
export function ReputationSetupResponsesSection({
  initialLocationId,
}: {
  initialLocationId?: string;
}) {
  const dict = useDictionary();
  const d = dict.reputation.setup.responses;
  const editorDict = dict.locations.detail.aiReplies;
  const workspaceId = useCurrentWorkspace()?.id;
  const queryClient = useQueryClient();
  const { locations } = useLocations(workspaceId, { limit: 100 });

  const removeOverrideMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (locationId: string) => {
      if (!workspaceId) return;
      await LocationsApi.update({
        path: { workspaceId, id: locationId },
        body: { replyPromptConfig: undefined },
      });
    },
    onSuccess: (_, locationId) => {
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, locationId),
      });
    },
    onError: () => toast.error(d.removeOverrideError),
  });

  if (!workspaceId) return null;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <ReplyStyleEditor
        workspaceId={workspaceId}
        scope="workspace"
        dict={editorDict}
      />

      <div className="flex flex-col gap-2">
        <SettingsGroupLabel>{d.overridesTitle}</SettingsGroupLabel>
        <SetupOverrideList
          locations={locations}
          isOverride={hasReplyPromptOverride}
          initialLocationId={initialLocationId}
          labels={{
            empty: d.noOverrides,
            add: d.addOverride,
            remove: d.removeOverride,
            overrideTag: d.overrideTag,
            location: d.locationLabel,
            locationPlaceholder: d.locationPlaceholder,
            continue: d.continueOverride,
            cancel: d.cancel,
          }}
          renderEditor={(loc) => (
            <ReplyStyleEditor
              workspaceId={workspaceId}
              locationId={loc.id}
              scope="location"
              dict={editorDict}
            />
          )}
          onRemove={(id) => removeOverrideMutation.mutate(id)}
          removing={removeOverrideMutation.isPending}
        />
      </div>

      <ReputationSetupAutoReplyDefaults workspaceId={workspaceId} />

      <ReputationSetupAutoReplyOverrides
        workspaceId={workspaceId}
        initialLocationId={initialLocationId}
      />
    </div>
  );
}
